import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDash } from "../ctx";
import { Activity, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { HowPulseWorks } from "@/components/pulse/HowPulseWorks";
import { PulseCountsLine } from "./PortfolioPulse";

const CARD = "bg-white rounded-2xl border border-[#E8E2D9]";

// Redline Admin → Pulse: platform default weights/thresholds + every portfolio with red communities.
export default function AdminPulse() {
  const { enterCompanyView } = useDash();
  const [data, setData] = useState(null);
  const [how, setHow] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/pulse").then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load")), []);
  useEffect(() => { load(); }, [load]);
  const recompute = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/pulse/recompute", {}); toast.success(`Recomputed ${r.data.computed} communit${r.data.computed === 1 ? "y" : "ies"}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Recompute failed"); }
    finally { setBusy(false); }
  };
  const d = data || {};
  const w = d.defaults?.weights || {};
  return (
    <div className="max-w-[1100px] space-y-6" data-testid="admin-pulse">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-redline-600 mb-1.5">Redline Admin</div>
          <PageTitle title="Pulse across portfolios" about={<>Pulse itself lives inside each portfolio (Portfolios → View as portfolio admin → Pulse) and inside each community (sidebar → Pulse). This is Redline's view: which portfolios have communities in the red, and the defaults every portfolio starts from. {d.scored ?? "—"} of {d.total ?? "—"} communities have the add-on on · {d.snapshots ?? "—"} daily snapshots stored.</>} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PulseCountsLine counts={d.counts} />
          <button type="button" data-testid="admin-pulse-recompute" onClick={recompute} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E2D9] bg-white px-3 py-1.5 text-[13px] font-bold hover:border-ink disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" strokeWidth={2} />} Recompute now
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3"><h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378]">Portfolios</h2><span className="h-px flex-1 bg-[#E5E0D8]" /></div>
        {!data ? (
          <div className="flex justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        ) : (d.companies || []).length === 0 ? (
          <div className={`${CARD} p-8 text-center text-sm text-[#78716C]`} data-testid="admin-pulse-no-companies">No portfolio has Pulse on yet.</div>
        ) : (
          <div className={`${CARD} overflow-hidden`} data-testid="admin-pulse-companies">
            {d.companies.map((c) => (
              <button key={c.company_id} type="button" data-testid={`admin-pulse-company-${c.company_id}`} onClick={() => enterCompanyView({ id: c.company_id, name: c.name }, "/dashboard/portfolio-pulse")}
                className="w-full text-left grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 sm:gap-4 items-center px-5 py-4 border-b border-[#F3EFE9] last:border-0 hover:bg-[#FAF8F5]">
                <div className="min-w-0">
                  <div className="font-bold text-[15px] text-[#1C1917]">{c.name} <span className="text-xs text-[#8C827A] font-semibold">· {c.communities} scored{c.settings_source === "company" ? " · own tuning" : " · Redline defaults"}</span></div>
                  <div className="text-xs mt-0.5 text-[#57534E]">
                    {c.red.length ? <span className="text-[#B91C1C] font-bold">Red: {c.red.map((r) => `${r.name} (${r.score})`).join(", ")}</span> : <span className="text-[#8C827A]">No red communities</span>}
                  </div>
                </div>
                <PulseCountsLine counts={c.counts} />
                <span className="text-xs font-bold text-[#1C1917]">Open Pulse →</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={CARD} data-testid="admin-pulse-defaults">
        <div className="px-5 py-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4 border-b border-[#F3EFE9]">
          <div className="min-w-0">
            <div className="font-heading font-bold text-[#1C1917] text-sm flex items-center gap-2"><Settings2 className="h-4 w-4 text-[#8C827A]" /> Defaults every portfolio starts from</div>
            <div className="text-xs text-[#57534E] mt-1">A portfolio's administrator can tune these for their own book (How Pulse works → Tune). Portfolios that haven't follow what's here.</div>
            {d.defaults?.updated_at && <div className="text-[11px] text-[#A8A29E] mt-1">Changed by {d.defaults.updated_by} · {new Date(d.defaults.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}
          </div>
          <button type="button" data-testid="admin-pulse-tune" onClick={() => setHow(true)} className="self-start sm:self-auto rounded-full bg-ink text-white px-4 py-2 text-sm font-bold hover:bg-ink-700">Edit defaults</button>
        </div>
        <div className="sm:hidden divide-y divide-[#F3EFE9]" data-testid="admin-pulse-defaults-mobile">
          {(d.signals || []).map((sg) => {
            const u = sg.unit === "stars" ? "★" : "%";
            const hi = sg.better === "high";
            return (
              <div key={sg.key} className="px-5 py-3">
                <div className="font-semibold text-[#1C1917] text-sm">{sg.label}{sg.module && <span className="ml-2 text-[10px] font-bold text-[#8C827A] uppercase">only where Requests is on</span>}</div>
                <div className="text-[11px] text-[#8C827A] mt-0.5">{sg.hint}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[12px] font-mono">
                  <span className="rounded-full bg-[#FAF8F5] border border-[#E8E2D9] px-2.5 py-0.5"><span className="text-[#8C827A]">weight</span> <b>{w[sg.key] ?? "—"}</b></span>
                  <span className="rounded-full bg-[#F0FDF4] border border-[#BBF7D0] px-2.5 py-0.5 whitespace-nowrap"><span className="text-[#15803D]">healthy</span> {hi ? "≥" : "≤"} {d.defaults?.lines?.[sg.key]?.healthy}{u}</span>
                  <span className="rounded-full bg-[#FDF2F2] border border-[#FECACA] px-2.5 py-0.5 whitespace-nowrap"><span className="text-[#B91C1C]">trouble</span> {hi ? "≤" : "≥"} {d.defaults?.lines?.[sg.key]?.trouble}{u}</span>
                </div>
              </div>
            );
          })}
          {d.defaults?.thresholds && <div className="px-5 py-3 text-[12px] font-mono text-[#57534E]">Bands · red below {d.defaults.thresholds.red_below} · green from {d.defaults.thresholds.green_from}</div>}
        </div>
        <table className="hidden sm:table w-full text-[13px]">
          <thead><tr className="text-[10px] uppercase tracking-[0.06em] text-[#8C827A]"><th className="text-left px-5 py-2 font-bold">Signal</th><th className="text-right px-3 py-2 font-bold">Weight</th><th className="text-right px-3 py-2 font-bold">Healthy</th><th className="text-right px-5 py-2 font-bold">Trouble</th></tr></thead>
          <tbody>
            {(d.signals || []).map((sg) => {
              const u = sg.unit === "stars" ? "★" : "%";
              const hi = sg.better === "high";
              return (
                <tr key={sg.key} className="border-t border-[#F3EFE9]" data-testid={`admin-pulse-default-${sg.key}`}>
                  <td className="px-5 py-2"><span className="font-semibold text-[#1C1917]">{sg.label}</span>{sg.module && <span className="ml-2 text-[10px] font-bold text-[#8C827A] uppercase">only where Requests is on</span>}<span className="block text-[11px] text-[#8C827A]">{sg.hint}</span></td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{w[sg.key] ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{hi ? "≥" : "≤"} {d.defaults?.lines?.[sg.key]?.healthy}{u}</td>
                  <td className="px-5 py-2 text-right font-mono whitespace-nowrap">{hi ? "≤" : "≥"} {d.defaults?.lines?.[sg.key]?.trouble}{u}</td>
                </tr>
              );
            })}
            {d.defaults?.thresholds && (
              <tr className="border-t-2 border-[#D8CEC1]"><td className="px-5 py-2 font-semibold text-[#1C1917]" colSpan={2}>Bands</td><td className="px-3 py-2 text-right font-mono whitespace-nowrap" colSpan={2}>red below {d.defaults.thresholds.red_below} · green from {d.defaults.thresholds.green_from}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(d.standalone || []).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3"><h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378]">Standalone communities</h2><span className="h-px flex-1 bg-[#E5E0D8]" /></div>
          <div className={`${CARD} overflow-hidden`} data-testid="admin-pulse-standalone">
            {d.standalone.map((r) => (
              <div key={r.tenant_id} className="grid grid-cols-[1fr_auto] gap-4 items-center px-5 py-3 border-b border-[#F3EFE9] last:border-0 text-sm">
                <span className="font-semibold text-[#1C1917] flex items-center gap-2"><Activity className="h-4 w-4 text-[#8C827A]" /> {r.name}</span>
                <span className="text-xs font-bold text-[#8C827A] uppercase">{!r.snapshot?.band ? "Quiet" : `${r.snapshot.band} · ${r.snapshot.score}`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <HowPulseWorks open={how} onOpenChange={setHow} rows={[]} signals={d.signals} settings={d.defaults ? { weights: d.defaults.weights, lines: d.defaults.lines, thresholds: d.defaults.thresholds, source: "platform" } : null}
        canTune mode="platform" onSaved={load} />
    </div>
  );
}
